<mat-card style="width: min-content; max-width: 100%;">
  <mat-card-header>
    <div class="g-just-flex flex-between w-100 flex-wrap gap-20">
      <div class="left">
        <mat-card-title>Admission Locations</mat-card-title>
        <mat-card-subtitle>
          Manage wards, rooms, beds, and other physical locations used for patient admission.
        </mat-card-subtitle>
      </div>
      <div class="right gap-10 g-just-flex" style="flex-wrap: wrap; flex: 1 1 auto; justify-content: flex-end;">
        @if (canExportLocation) {
        <a mat-flat-button extended class="secondary-button small-button" (click)="onExportLocations()">
          <mat-icon>file_download</mat-icon>
          Export
        </a>
        }
        @if (canAddLocation) {
        <a mat-flat-button (click)="onAddAdmissionLocation()">
          <mat-icon>add</mat-icon>
          Add Admission Location
        </a>
        }
      </div>
    </div>
  </mat-card-header>

  <mat-card-content class="my-12">
    <mat-divider></mat-divider>

    <!-- Indeterminate progress bar while fetching all pages -->
    @if (loading$ | async) {
    <mat-progress-bar mode="indeterminate" style="margin-bottom: 4px;"></mat-progress-bar>
    }

    <!-- Tab buttons -->
    <div class="my-15">
      <div class="rounded-buttons-group">
        <button (click)="setPhysicalTypeFilter('ward')" title="Wards"
          [ngClass]="{'selected': physicalTypeFilter.value === 'ward'}">
          Wards
        </button>
        <button (click)="setPhysicalTypeFilter('room')" title="Rooms"
          [ngClass]="{'selected': physicalTypeFilter.value === 'room'}">
          Rooms
        </button>
        <button (click)="setPhysicalTypeFilter('bed')" title="Beds"
          [ngClass]="{'selected': physicalTypeFilter.value === 'bed'}">
          Beds
        </button>
        <button (click)="setPhysicalTypeFilter('all')" title="All"
          [ngClass]="{'selected': physicalTypeFilter.value === 'all'}">
          All
        </button>
      </div>

    </div>

    <!-- Search bar + table-header dropdown filters (below tabs) -->
    <app-table-header
      [searchableFields]="searchableFields"
      [selectedSearchField]="selectedSearchField"
      [patientsTableFilterArray]="locationTableFilterArray"
      [patientsFiltersFormControlObject]="locationFiltersFormControlObject"
      (searchCriteriaChanged)="onSearchCriteriaChanged($event)">
    </app-table-header>

    <!-- Table + paginator stacked in a column -->
    <div class="table-or-empty" style="background-color: var(--mat-sys-surface); flex-direction: column;">
      @if (locationSearch.dataSource.data && locationSearch.dataSource.data.length > 0) {

      <table style="min-width: 630px; width: unset" mat-table [dataSource]="locationSearch.dataSource"
        class="mat-elevation-z8 demo-table">

        <!-- Name Column with Parent -->
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>Name</th>
          <td mat-cell *matCellDef="let location" style="padding-top: 8px; padding-bottom: 8px;">
            <div class="top">
              <span class="restrict-length">{{ location.name | na | titlecase }}</span>
            </div>
            @if (location._hierarchyString) {
            <div class="subtitle-text-color-2 small-subtitle">
              <span>{{ location._hierarchyString }}</span>
            </div>
            } @else if (location.rawResource?.partOf?.reference) {
            <div class="subtitle-text-color-2 small-subtitle">
              <span [appReferenceDisplay]="location.rawResource.partOf.reference" [instantFetchAndReplace]="true"></span>
            </div>
            }
          </td>
        </ng-container>

        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let location">
            @if (location.status) {
            <div [appChips]="location.status" [fontSize]="'14px'"></div>
            } @else {
            <span class="subtitle-text-color-2">N/A</span>
            }
          </td>
        </ng-container>

        <!-- Physical Type Column -->
        <ng-container matColumnDef="physicalType">
          <th mat-header-cell *matHeaderCellDef>Type</th>
          <td mat-cell *matCellDef="let location">
            {{ location.rawResource?.physicalType | codeableConcept2 | na | titlecase }}
          </td>
        </ng-container>

        <!-- Occupancy Status Column (Beds only) -->
        <ng-container matColumnDef="occupancyStatus">
          <th mat-header-cell *matHeaderCellDef>Occupancy</th>
          <td mat-cell *matCellDef="let location">
            @if (location['Occupancy Status']) {
              <span [ngClass]="{
                'status-chip-active': location['Occupancy Status'] === 'Unoccupied' || location['Occupancy Status'] === 'Free',
                'status-chip-inactive': location['Occupancy Status'] === 'Occupied' || location['Occupancy Status'] === 'Inactive',
                'status-chip-suspended': location['Occupancy Status'] === 'Housekeeping' || location['Occupancy Status'] === 'Closed' || location['Occupancy Status'] === 'Isolated' || location['Occupancy Status'] === 'Contaminated' || location['Occupancy Status'] === 'Suspended'
              }">
                {{ location['Occupancy Status'] }}
              </span>
            } @else {
              <span class="subtitle-text-color-2">—</span>
            }
          </td>
        </ng-container>

        <!-- Available Beds Column -->
        <ng-container matColumnDef="availableBeds">
          <th mat-header-cell *matHeaderCellDef>Available Beds</th>
          <td mat-cell *matCellDef="let location">
            @if (location.availableBeds) {
              <span [appEmphasis]="'grey'">{{ location.availableBeds }}</span>
            } @else if (loading$ | async) {
              <span class="subtitle-text-color-2" style="font-style: italic; font-size: 12px;">loading…</span>
            } @else {
              <span class="subtitle-text-color-2">—</span>
            }
          </td>
        </ng-container>

        <!-- Last Updated Column -->
        <ng-container matColumnDef="meta">
          <th mat-header-cell *matHeaderCellDef>Last Updated</th>
          <td mat-cell *matCellDef="let location" style="padding-top: 4px; padding-bottom: 4px;">
            <span class="restrict-length">{{ location.rawResource?.meta?.lastUpdated | date: 'mediumDate' | na }}</span>
            <br>
            <span class="restrict-length subtitle-text-color-2 small-subtitle">
              {{ location.rawResource?.meta?.lastUpdated | date: 'shortTime' | na }}
            </span>
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let location">
            <button mat-icon-button color="primary" (click)="showLocationDetails(location); $event.stopPropagation()" title="View Details">
              <mat-icon>visibility</mat-icon>
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns" matHeaderRowDefSticky="true"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;" style="cursor: pointer;"
            (click)="showRow(row)">
        </tr>
      </table>

      <!-- Paginator sits below the table -->
      <mat-paginator
        [length]="total$ | async"
        [pageSize]="pageSize"
        [pageIndex]="paginatorPageIndex"
        [pageSizeOptions]="[25, 50, 100]"
        (page)="onPageChange($event)"
        showFirstLastButtons>
      </mat-paginator>

      } @else {
      <div class="g-just-flex flex-column align-items-center py-40" style="text-align:center;">
        <app-empty-state [icon]="'LOCATION_CITY'" [title]="'No Locations Found'"
          [subtitle]="'No admission locations are registered yet.'">
        </app-empty-state>
      </div>
      }
    </div>
  </mat-card-content>
</mat-card>